import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalendarIcon, UserIcon, PhoneIcon } from '@heroicons/react/24/outline';
import FollowUpService from '../services/followUpService';
import EmployeeDashboardService from '../services/employeeDashboardService';
import api from '../utils/axios';

const FollowUpModal = ({ 
  isOpen, 
  onClose, 
  lead, 
  onFollowUpAdded,
  currentUser = null 
}) => {
  const [formData, setFormData] = useState({
    type: 'call',
    summary: '',
    notes: '',
    nextFollowUpDate: '',
    nextStep: '',
    priority: 'medium',
    updateLeadStatus: false,
    newLeadStatus: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [availableTransitions, setAvailableTransitions] = useState([]);

  // Fetch lead statuses when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: 'call',
        summary: '',
        notes: '',
        nextFollowUpDate: '',
        nextStep: '',
        priority: 'medium',
        updateLeadStatus: false,
        newLeadStatus: ''
      });
      setError('');
      fetchLeadStatuses();
    }
  }, [isOpen]);

  // Update available transitions when lead status changes
  useEffect(() => {
    if (lead && leadStatuses.length > 0) {
      const currentStatus = leadStatuses.find(status => status.name === lead.status);
      if (currentStatus && currentStatus.canTransitionTo && currentStatus.canTransitionTo.length > 0) {
        const transitions = leadStatuses.filter(status => 
          currentStatus.canTransitionTo.includes(status.name)
        );
        setAvailableTransitions(transitions);
      } else {
        // If no transitions defined, allow all statuses
        setAvailableTransitions(leadStatuses);
      }
    }
  }, [lead, leadStatuses]);

  const fetchLeadStatuses = async () => {
    try {
      const response = await api.leadsApi.get('/lead-statuses');
      if (response.data.success) {
        setLeadStatuses(response.data.statuses);
      }
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.summary.trim()) {
      setError('Summary is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const followUpData = {
        ...formData,
        userId: currentUser?.id || currentUser?._id
      };

      const result = await FollowUpService.addFollowUp(lead._id, followUpData);

      if (result.success) {
        // Update lead status if requested
        if (formData.updateLeadStatus && formData.newLeadStatus) {
          const statusResult = await EmployeeDashboardService.updateLeadStatus(
            lead._id, 
            formData.newLeadStatus, 
            `Status updated during follow-up: ${formData.summary}`
          );
          
          if (!statusResult.success) {
            console.warn('Follow-up added but failed to update lead status:', statusResult.message);
          }
        }
        
        onFollowUpAdded?.(result.data);
        onClose();
      } else {
        setError(result.message || 'Failed to add follow-up');
      }
    } catch (error) {
      console.error('Error adding follow-up:', error);
      setError('Failed to add follow-up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  const followUpTypes = FollowUpService.getFollowUpTypes();
  const priorityOptions = FollowUpService.getPriorityOptions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Follow-up</h2>
            <p className="text-sm text-gray-600 mt-1">
              Lead: {lead?.name || 'Unknown'} • {lead?.company || 'No Company'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Lead Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center">
                <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Name:</span>
                <span className="ml-2 font-medium">{lead?.name || 'N/A'}</span>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Phone:</span>
                <span className="ml-2 font-medium">{lead?.phone || 'N/A'}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{lead?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Follow-up Type and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Follow-up Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {followUpTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {priorityOptions.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Summary *
            </label>
            <input
              type="text"
              name="summary"
              value={formData.summary}
              onChange={handleInputChange}
              placeholder="Brief summary of the follow-up..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="4"
              placeholder="Detailed notes about the follow-up..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Next Follow-up Date and Next Step */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Follow-up Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="nextFollowUpDate"
                  value={formData.nextFollowUpDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <CalendarIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Step
              </label>
              <input
                type="text"
                name="nextStep"
                value={formData.nextStep}
                onChange={handleInputChange}
                placeholder="What's the next action to take?"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Lead Status Update Section */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3 mb-4">
              <input
                type="checkbox"
                id="updateLeadStatus"
                name="updateLeadStatus"
                checked={formData.updateLeadStatus}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="updateLeadStatus" className="text-sm font-medium text-gray-700">
                Update Lead Status
              </label>
            </div>
            
            {formData.updateLeadStatus && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Lead Status
                </label>
                {lead && leadStatuses.length > 0 && (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Current Status: <span className="font-medium">{lead.status}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {availableTransitions.length > 0 
                        ? `${availableTransitions.length} transition(s) available based on status flow rules`
                        : 'No valid transitions defined for current status'
                      }
                    </p>
                  </div>
                )}
                <select
                  name="newLeadStatus"
                  value={formData.newLeadStatus}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={formData.updateLeadStatus}
                >
                  <option value="">Select new status...</option>
                  {availableTransitions.map((status) => (
                    <option key={status._id} value={status.name}>
                      <span className={`inline-block w-2 h-2 rounded-full bg-${status.color}-500 mr-2`}></span>
                      {status.label}
                    </option>
                  ))}
                  {availableTransitions.length === 0 && leadStatuses.length > 0 && (
                    <option disabled>No valid transitions available</option>
                  )}
                </select>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding...' : 'Add Follow-up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FollowUpModal;
